/**
 * The tab bar of the credit-limits page: Overview | Requests | Approval rules (masterplan credit-limits-v3, §4 Web,
 * package A4). The page owns the selected tab and its deep link (`?tab=`, A5); this bar only draws and moves.
 *
 * The WAI-ARIA tabs pattern with automatic activation: one `role="tablist"`, each tab a `<button role="tab">` with
 * `aria-selected` and `aria-controls`; only the selected tab is in the tab order; ArrowLeft/ArrowRight move and
 * select (wrapping), Home and End go to the ends. The panel takes `role="tabpanel"`, `id` and `aria-labelledby` from
 * `climTabPanelProps`. Look: the underline tabs of inventory-app `web/src/styles/kit.css` `.tab` (read 2026-10-22),
 * drawn by `.clim .tabs` / `.clim .tab` in styles/clim/kit.css.
 */
import { useRef, type KeyboardEvent } from 'react';

export interface ClimTab<T extends string> {
    id: T;
    label: string;
    /** A count in amber beside the label (open requests waiting for this person); 0 or absent draws none. */
    count?: number;
    /** Name of the count for a screen reader: «3 waiting for you». */
    countLabel?: string;
}

export function climTabId(prefix: string, id: string): string {
    return `${prefix}-tab-${id}`;
}

export function climPanelId(prefix: string, id: string): string {
    return `${prefix}-panel-${id}`;
}

/** The attributes of the panel that belongs to the selected tab (spread by hand; the scans read written attributes). */
export function climTabPanelProps(prefix: string, id: string): { id: string; role: 'tabpanel'; 'aria-labelledby': string; tabIndex: 0 } {
    return { id: climPanelId(prefix, id), role: 'tabpanel', 'aria-labelledby': climTabId(prefix, id), tabIndex: 0 };
}

/** The tab a key moves to from `current`, or null for a key the bar does not handle. Pure, tested without a browser. */
export function tabAfterKey<T extends string>(ids: readonly T[], current: T, key: string): T | null {
    if (ids.length === 0) return null;
    const i = Math.max(0, ids.indexOf(current));
    if (key === 'ArrowRight') return ids[(i + 1) % ids.length]!;
    if (key === 'ArrowLeft') return ids[(i - 1 + ids.length) % ids.length]!;
    if (key === 'Home') return ids[0]!;
    if (key === 'End') return ids[ids.length - 1]!;
    return null;
}

interface Props<T extends string> {
    tabs: readonly ClimTab<T>[];
    value: T;
    onChange: (id: T) => void;
    /** The tablist's name: «Credit limits». */
    label: string;
    /** Prefix of the tab and panel ids; one per page. */
    idPrefix?: string;
}

export function ClimTabs<T extends string>({ tabs, value, onChange, label, idPrefix = 'clim' }: Props<T>) {
    const listRef = useRef<HTMLDivElement | null>(null);
    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const next = tabAfterKey(tabs.map(t => t.id), value, e.key);
        if (next === null) return;
        e.preventDefault();
        if (next !== value) onChange(next);
        const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons?.[tabs.findIndex(t => t.id === next)]?.focus();
    };
    return (
        <div ref={listRef} className="tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
            {tabs.map(t => {
                const selected = t.id === value;
                return (
                    <button
                        key={t.id}
                        id={climTabId(idPrefix, t.id)}
                        type="button"
                        role="tab"
                        className="tab"
                        aria-selected={selected}
                        aria-controls={climPanelId(idPrefix, t.id)}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => { if (!selected) onChange(t.id); }}
                    >
                        {t.label}
                        {t.count ? <span className="cnt" aria-label={t.countLabel ?? String(t.count)}>{t.count}</span> : null}
                    </button>
                );
            })}
        </div>
    );
}
